import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { generatedOpenAPI } from '../generated/generatedOpenAPI';
import antibodyAnalysisReducer from './slices/antibodyAnalysis.slice';
import authReducer from './slices/auth.slice';
import clonotypeReducer from './slices/clonotypes.slice';
import preprocessingReducer from './slices/preprocessing.slice';
import uploadsReducer from './slices/uploads.slice';
import { stellarApiEnhanced } from '../api/enhancedApiCalls';

const rootReducer = combineReducers({
  auth: authReducer,
  clonotypes: clonotypeReducer,
  preprocessing: preprocessingReducer,
  antibodyAnalysis: antibodyAnalysisReducer,
  uploads: uploadsReducer,
  [generatedOpenAPI.reducerPath]: generatedOpenAPI.reducer,
});

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['clonotypes', 'preprocessing', 'antibodyAnalysis'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(generatedOpenAPI.middleware),
});

export type AppState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

const persistor = persistStore(store);
export { store, persistor };

// rename the generated hooks to use* for easier readability
export const {
  useInitializeExperimentApiStellarExperimentsInitPostQuery: useInitializeExperiment,
  useLazyInitializeExperimentApiStellarExperimentsInitPostQuery: useLazyInitializeExperiment,
  useCreateExperimentApiStellarExperimentsPostMutation: useCreateExperiment,
  useGetExperimentApiStellarExperimentsIdGetQuery: useGetExperiment,
  useGetExperimentsApiStellarExperimentsGetQuery: useGetExperiments,
  useDeleteExperimentApiStellarExperimentsIdDeleteMutation: useDeleteExperiment,
  useUpdateExperimentApiStellarExperimentsIdPutMutation: useUpdateExperiment,
  usePruneExperimentsApiStellarExperimentsPrunePostMutation: usePruneExperiments,
  useLazyGetExperimentApiStellarExperimentsIdGetQuery: useLazyGetExperiment,
  useLazyGetExperimentsApiStellarExperimentsGetQuery: useLazyGetExperiments,
  useUploadFileApiStellarUploadPostMutation: useUploadFile,
  useDeleteFileApiStellarUploadExperimentIdFileNameDeleteMutation: useDeleteFile,
  useValidateAntibodyCsvFileApiStellarUploadValidateExperimentIdFileNamePostMutation: useValidateAntibodyFile,
  useGetValidationResultApiStellarUploadExperimentIdValidationResultsGetQuery: useGetValidationResult,
  useLazyGetValidationResultApiStellarUploadExperimentIdValidationResultsGetQuery: useLazyGetValidationResult,
  useGetSummaryDataApiStellarExperimentsIdSummaryDataGetQuery: useGetSummaryData,
  useGetCellsApiStellarExperimentsIdCellsGetQuery: useGetCells,
  useGetCellsWithClonotypeApiStellarExperimentsIdClonotypeDefinitionCellsGetQuery: useGetCellsWithClonotype,
  useGetClonotypeSizePerCellApiStellarExperimentsIdCellClonotypesClonotypeDefinitionGetQuery: useGetClonotypeSizePerCell,
  useGetNodesApiStellarExperimentsIdNodesClonotypeDefinitionGetQuery: useGetNodes,
  useGetUniqueClonotypeidsApiStellarExperimentsIdClonotypeIdsClonotypeDefinitionGetQuery: useGetUniqueClonotypeIds,
  useGetAntibodySummaryDataApiStellarExperimentsIdClonotypeDefinitionAntibodySummaryDataGetQuery: useGetAntibodySummaryData,
  useGetRepertoireDataApiStellarExperimentsIdRepertoireDataGetQuery: useGetRepertoireData,
  useLazyGetRepertoireDataApiStellarExperimentsIdRepertoireDataGetQuery: useLazyGetRepertoireData,
  useLazyGetSummaryDataApiStellarExperimentsIdSummaryDataGetQuery: useLazyGetSummaryData,
  useGetTaskStatusForAnalysisApiStellarExperimentsExperimentIdTaskGetQuery: useGetAnalysisTaskStatusForExperiment,
  useGetProcessingStatsApiStellarExperimentsExperimentIdProcessingStatsGetQuery: useGetProcessingStats,
  useGetPendingProcessingStepStatusApiStellarExperimentsExperimentIdPendingProcessingStatusGetQuery: useGetPendingProcessingStatus,
  useStartClonotypeAnalysisApiStellarAnalysisExperimentIdClonotypeAnalysisPostMutation: useStartClonotypeAnalysis,
  useDownloadFileApiStellarExperimentsExperimentIdDownloadGetQuery: useDownloadFile,
  useGetExperimentExportsApiStellarExportExperimentUuidExportsGetQuery: useListExports,
  useCreateExportApiStellarExportExperimentUuidPostMutation: useCreateExport,
  useModifyExportApiStellarExportExportUuidPatchMutation: useUpdateExport,
  useDeleteExportApiStellarExportExportUuidDeleteMutation: useDeleteExport,
  useSearchGenesApiStellarSearchExperiementExperimentIdGenesGetQuery: useSearchGenes,
  useGetBuildVersionApiStellarConfigurationBuildVersionGetQuery: useGetBuildVersion,
  useListProfilingReportsApiStellarProfilingReportsGetQuery: useListProfilingReports,
} = stellarApiEnhanced;
